import React from 'react';
import { IconChecked } from '../../../components/Common/IconChecked';
import BookCoverImage from '../BookCoverImage';
import { BookModel } from '../../../interfaces/book.interface';
import { CurrencySymbol } from '../../../config/countries';

interface PrintedBookSummaryProps {
  bookQty: number;
  bookCost: number;
  additionalPageCost: number;
  totalPages: number;
  bookTotalCost: number;
  shippingCost: number;
  discount: number | null;
  netCost: number;
  currencySymbol?: CurrencySymbol | null;
  bookDetails: BookModel | null;
}

const PrintedBookSummary: React.FC<PrintedBookSummaryProps> = ({
  bookQty,
  bookCost,
  additionalPageCost,
  totalPages,
  bookTotalCost,
  shippingCost,
  discount,
  netCost,
  currencySymbol = '$',
  bookDetails,
}) => {
  return (
    <div className="flex flex-col justify-between rounded-3xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 sm:p-10">
      <div>
        <div className="flex justify-between flex-col items-center">
          <h3 id="tier-hobby" className=" text-left text-3xl font-semibold leading-7 text-storyblue">
            Printed Book
          </h3>

          <div className="flex  flex-col items-end">
            <div className="w-[50%] border-yellow-700"></div>
            <div>
              <h1 className="text-storyblue text-base mt-2 border-red-300 flex justify-between">
                <span>Book Qty:</span> {bookQty}
              </h1>
              <h1 className="text-storyblue text-base mt-2 flex justify-between">
                <span>Book Cost:</span> {bookCost === 0 ? 'Free' : `${currencySymbol}${bookCost}`}
              </h1>
              {additionalPageCost > 0 && totalPages !== 16 && bookCost! > 0 && (
                <h1 className="text-storyblue text-base mt-2 flex justify-between">
                  <span>Additional Pages Cost:</span> {currencySymbol} {additionalPageCost}
                </h1>
              )}

              <hr className="mt-4" />

              <h1 className="text-storyblue text-base mt-2 flex justify-between">
                <span> Book Total Cost:</span>{' '}
                <span className="text-lg font-bold tracking-tight text-gray-900">
                  {Number(bookTotalCost) === 0 ? 'Free' : `${currencySymbol}${Number(bookTotalCost).toFixed(2)}`}
                </span>
              </h1>

              <h1 className="text-storyblue text-base mt-2 flex gap-2 justify-between">
                <span> Shipping & Handling:</span>
                {shippingCost === 0.0 ? 'Free' : `${currencySymbol}${shippingCost}`}
              </h1>

              {discount && (
                <>
                  <hr className="mt-4" />

                  <h1 className="text-storyblue text-base mt-2 flex justify-between items-center">
                    <span>Discount Amount:</span>{' '}
                    <span className="text-lg font-bold tracking-tight text-gray-900">
                      {currencySymbol} {discount}
                    </span>
                  </h1>
                </>
              )}

              <>
                <hr className="mt-4" />

                <h1 className="text-storyblue text-base mt-2 flex justify-between items-center">
                  <span>Net Amount:</span>{' '}
                  <span className="font-bold tracking-tight text-gray-900 text-xs italic">
                    {netCost === 0.0 ? 'Free' : `${currencySymbol}${netCost}`}
                  </span>
                </h1>
              </>
            </div>
          </div>
        </div>
        <BookCoverImage bookDetails={bookDetails} />
        <div className="w-full flex justify-center items-center gap-2" title="Book Cover Size">
          <h1 className="text-storygreen text-sm">
            <strong>Size : </strong>8.5" x 8.5" (21.59 cm x 21.59 cm)
          </h1>
        </div>
        <ul role="list" className="mt-10 space-y-4 text-sm leading-6 text-gray-600">
          <li className="flex gap-x-3 text-left">
            {IconChecked()}A {bookDetails?.pageTotal} pages beautifully printed book, storytelling in you chosen art
            style. Additional pages will incur separate charges.
          </li>
          <li className="flex gap-x-3 text-left">
            {IconChecked()}
            High-quality print, perfect for hands-on reading experiences. Shipped to your address.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PrintedBookSummary;
